import { useEffect, useState } from "react";
import FormInput from "../../molecules/FormInput";
import FormTemplate from "../../templates/FormTemplate";
import { Box, Grid } from "@mui/material";
import { SettingsService } from "../../../services/api";
import { useGlobalContext } from "../../../stores/Global";
import useRefresh from "../../../hooks/useRefresh";
import { useTranslation } from "react-i18next";
import { useMutationState } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import FieldLabel from "../../atoms/FieldLabel";

export default function FtpSettingsPage() {
  const { t } = useTranslation("Settings");
  const { context } = useGlobalContext();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(false);
  const [host, setHost] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [uploadPath, setUploadPath] = useState("");
  const [port, setPort] = useState(21);
  const [secure, setSecure] = useState(false);

  const [loading] = useMutationState({
    filters: { mutationKey: ["settings"] },
    select: (mutation) => mutation.state.status === "pending",
  });

  const handleSubmit = async () => {
    if (!context.settings) return;
    await SettingsService.updateSettings({
      appSettings: {
        ...context.settings,
        ftp: {
          enabled,
          host,
          username,
          password,
          uploadPath,
          port,
          secure,
        },
      },
    });
    refresh();
  };

  const handleClose = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (!context.settings) return;
    setEnabled(context.settings.ftp.enabled);
    setHost(context.settings.ftp.host);
    setUsername(context.settings.ftp.username);
    setPassword(context.settings.ftp.password);
    setPort(context.settings.ftp.port);
    setSecure(context.settings.ftp.secure);
    setUploadPath(context.settings.ftp.uploadPath);
  }, [context.settings]);

  return (
    <FormTemplate
      title={t("FTP.Title")}
      disableCancelButton
      submitButtonText={t("Save")}
      disableEnter
      onSubmit={handleSubmit}
      confirm
      loading={loading}
      onCancel={handleClose}
    >
      <Grid container alignItems={"center"} rowGap={3}>
        <Grid size={{ xs: 5, md: 3 }}>
          <FieldLabel children={t("Enabled")} />
        </Grid>
        <Grid size={{ xs: 7, md: 9 }}>
          <FormInput
            loading={loading}
            field="enabled"
            label={""}
            type="switch"
            value={enabled}
            onChange={(value) => setEnabled(value as boolean)}
          />
        </Grid>

        <Grid size={{ xs: 5, md: 3 }}>
          <FieldLabel children={t("Host")} />
        </Grid>
        <Grid size={{ xs: 7, md: 9 }}>
          <FormInput
            loading={loading}
            field="host"
            label={""}
            type="string"
            value={host}
            onChange={(value) => setHost(value as string)}
          />
        </Grid>

        <Grid size={{ xs: 5, md: 3 }}>
          <FieldLabel children={t("Username")} />
        </Grid>
        <Grid size={{ xs: 7, md: 3 }}>
          <FormInput
            loading={loading}
            field="username"
            label={""}
            type="string"
            value={username}
            onChange={(value) => setUsername(value as string)}
          />
        </Grid>
        <Grid size={{ md: 1 }}>
          <Box />
        </Grid>
        <Grid size={{ xs: 5, md: 2 }}>
          <FieldLabel children={t("Password")} />
        </Grid>
        <Grid size={{ xs: 7, md: 3 }}>
          <FormInput
            loading={loading}
            field="password"
            label={""}
            type="string"
            inputType="password"
            value={password}
            onChange={(value) => setPassword(value as string)}
          />
        </Grid>

        <Grid size={{ xs: 5, md: 3 }}>
          <FieldLabel children={t("UploadPath")} />
        </Grid>
        <Grid size={{ xs: 7, md: 3 }}>
          <FormInput
            loading={loading}
            field="uploadPath"
            label={""}
            placeholder={t("UploadPathPlaceholder")}
            type="string"
            value={uploadPath}
            onChange={(value) => setUploadPath(value as string)}
          />
        </Grid>
        <Grid size={{ md: 1 }}>
          <Box />
        </Grid>
        <Grid size={{ xs: 5, md: 2 }}>
          <FieldLabel children={t("Port")} />
        </Grid>
        <Grid size={{ xs: 7, md: 3 }}>
          <FormInput
            loading={loading}
            field="port"
            label={""}
            type="number"
            value={port}
            onChange={(value) => setPort(value as number)}
          />
        </Grid>

        <Grid size={{ xs: 5, md: 3 }}>
          <FieldLabel children={t("Secure")} />
        </Grid>
        <Grid size={{ xs: 7, md: 9 }}>
          <FormInput
            loading={loading}
            field="secure"
            label={""}
            type="switch"
            value={secure}
            onChange={(value) => setSecure(value as boolean)}
          />
        </Grid>
      </Grid>
    </FormTemplate>
  );
}
